import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";
import {
  readLegacyListlessGroupPlacement,
  type ListlessGroupPlacement,
} from "../lib/listlessGroupPlacement";

interface UIPreferences {
  showCreatedDateChip: boolean;
  showScheduleDatesChip: boolean;
  showSubtasksChip: boolean;
  showAttachmentsChip: boolean;
  showLinksChip: boolean;
  showDurationChip: boolean;
  showTagsChip: boolean;
  listlessGroupPlacement: ListlessGroupPlacement;
}

interface UIPreferencesContextType {
  preferences: UIPreferences;
  setPreferences: (preferences: Partial<UIPreferences>) => void;
  isLoading: boolean;
}

const UIPreferencesContext = createContext<
  UIPreferencesContextType | undefined
>(undefined);

const STORAGE_KEY = "judi-ui-preferences";

const DEFAULT_PREFERENCES: UIPreferences = {
  showCreatedDateChip: true,
  showScheduleDatesChip: true,
  showSubtasksChip: true,
  showAttachmentsChip: true,
  showLinksChip: true,
  showDurationChip: true,
  showTagsChip: true,
  listlessGroupPlacement: "end",
};

function listlessPlacementFromStore(parsed: {
  listlessGroupPlacement?: unknown;
}): ListlessGroupPlacement {
  if (
    parsed.listlessGroupPlacement === "start" ||
    parsed.listlessGroupPlacement === "end"
  ) {
    return parsed.listlessGroupPlacement;
  }
  return readLegacyListlessGroupPlacement() ?? "end";
}

function getInitialPreferences(): UIPreferences {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      return {
        showCreatedDateChip: parsed.showCreatedDateChip ?? true,
        showScheduleDatesChip: parsed.showScheduleDatesChip ?? true,
        showSubtasksChip: parsed.showSubtasksChip ?? true,
        showAttachmentsChip: parsed.showAttachmentsChip ?? true,
        showLinksChip: parsed.showLinksChip ?? true,
        showDurationChip: parsed.showDurationChip ?? true,
        showTagsChip: parsed.showTagsChip ?? true,
        listlessGroupPlacement: listlessPlacementFromStore(parsed),
      };
    } catch {
      // Invalid JSON, use defaults
    }
  }
  return {
    ...DEFAULT_PREFERENCES,
    listlessGroupPlacement: readLegacyListlessGroupPlacement() ?? "end",
  };
}

export function UIPreferencesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const [preferences, setPreferencesState] = useState<UIPreferences>(
    getInitialPreferences,
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      return;
    }

    setIsLoading(true);

    supabase
      .from("user_preferences")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.error("Failed to fetch chip preferences:", error);
          setIsLoading(false);
          return;
        }

        if (data) {
          setPreferencesState((prev) => {
            const merged: UIPreferences = {
              showCreatedDateChip: data.show_created_date_chip ?? true,
              showScheduleDatesChip: data.show_schedule_dates_chip ?? true,
              showSubtasksChip: data.show_subtasks_chip ?? true,
              showAttachmentsChip: data.show_attachments_chip ?? true,
              showLinksChip: data.show_links_chip ?? true,
              showDurationChip: data.show_duration_chip ?? true,
              showTagsChip: data.show_tags_chip ?? true,
              listlessGroupPlacement: prev.listlessGroupPlacement,
            };
            try {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
            } catch {
              // Private mode / blocked storage — keep the in-memory value.
            }
            return merged;
          });
        }

        setIsLoading(false);
      });
  }, [user]);

  const setPreferences = (newPreferences: Partial<UIPreferences>) => {
    setPreferencesState((prev) => {
      const updated = { ...prev, ...newPreferences };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch {
        // Private mode / blocked storage — keep the in-memory value.
      }

      if (user) {
        supabase
          .from("user_preferences")
          .upsert(
            {
              user_id: user.id,
              show_created_date_chip: updated.showCreatedDateChip,
              show_schedule_dates_chip: updated.showScheduleDatesChip,
              show_subtasks_chip: updated.showSubtasksChip,
              show_attachments_chip: updated.showAttachmentsChip,
              show_links_chip: updated.showLinksChip,
              show_duration_chip: updated.showDurationChip,
              show_tags_chip: updated.showTagsChip,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "user_id" },
          )
          .then(({ error }) => {
            if (error) {
              console.error("Failed to save chip preferences:", error);
            }
          });
      }

      return updated;
    });
  };

  return (
    <UIPreferencesContext.Provider
      value={{ preferences, setPreferences, isLoading }}
    >
      {children}
    </UIPreferencesContext.Provider>
  );
}

export function useUIPreferences() {
  const context = useContext(UIPreferencesContext);
  if (context === undefined) {
    throw new Error(
      "useUIPreferences must be used within a UIPreferencesProvider",
    );
  }
  return context;
}
